import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/api'
import type { User, LoginRequest, RegisterRequest, ChangePasswordRequest } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  const setAuthData = (userData: User, authToken: string) => {
    user.value = userData
    token.value = authToken
    localStorage.setItem('auth_token', authToken)
    localStorage.setItem('user_data', JSON.stringify(userData))
    
    // Configurar token na API
    authService.setAuthToken(authToken)
  }

  const clearAuthData = () => {
    user.value = null
    token.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    
    // Remover token da API
    authService.removeAuthToken()
  }

  const loadAuthFromStorage = () => {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('user_data')
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        user.value = userData
        token.value = storedToken
        
        // Configurar token na API
        authService.setAuthToken(storedToken)
      } catch (error) {
        console.error('Erro ao carregar dados de autenticação:', error)
        clearAuthData()
      }
    }
  }

  const login = async (credentials: LoginRequest) => {
    loading.value = true
    try {
      const response = await authService.login(credentials)
      
      if (response.success && response.data) {
        setAuthData(response.data.usuario, response.data.token)
        return { success: true }
      } else {
        return { 
          success: false, 
          error: response.error || 'Erro ao fazer login' 
        }
      }
    } catch (error: any) {
      console.error('Erro no login:', error)
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erro ao fazer login' 
      }
    } finally {
      loading.value = false
    }
  }

  const register = async (userData: RegisterRequest) => {
    loading.value = true
    try {
      const response = await authService.register(userData)
      
      if (response.success && response.data) {
        setAuthData(response.data.usuario, response.data.token)
        return { success: true }
      } else {
        return { 
          success: false, 
          error: response.error || 'Erro ao fazer registro' 
        }
      }
    } catch (error: any) {
      console.error('Erro no registro:', error)
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erro ao fazer registro' 
      }
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    try {
      await authService.logout()
    } catch (error) {
      console.error('Erro no logout:', error)
    } finally {
      clearAuthData()
      loading.value = false
    }
  }

  const getMe = async () => {
    try {
      const response = await authService.getMe()
      
      if (response.success && response.data) {
        user.value = response.data
        return { success: true }
      } else {
        clearAuthData()
        return { 
          success: false, 
          error: response.error || 'Erro ao buscar dados do usuário' 
        }
      }
    } catch (error: any) {
      console.error('Erro ao buscar dados do usuário:', error)
      clearAuthData()
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erro ao buscar dados do usuário' 
      }
    }
  }

  const changePassword = async (passwordData: ChangePasswordRequest) => {
    loading.value = true
    try {
      const response = await authService.changePassword(passwordData)
      
      if (response.success) {
        return { success: true, message: response.message }
      } else {
        return { 
          success: false, 
          error: response.error || 'Erro ao alterar senha' 
        }
      }
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error)
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erro ao alterar senha' 
      }
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    getMe,
    changePassword,
    loadAuthFromStorage,
    clearAuthData
  }
}) 